import { useEffect } from 'react';
import { Checkbox, Text } from '@belong/ui';
import { PaymentPreference } from 'models/enums';
import { FAILED_PAYMENTS_STRINGS } from '../failed-payments-strings';
import { mapPaymentAccountToPaymentMethodSummary } from '../failed-payments-utils';
import { FailedPaymentMethodSelector } from '../failed-payment-method-selector';
import { WireDetails } from './homeowner-change-payment-method-subsection';
import type { HomeownerChangePaymentMethodSubsectionProps } from './homeowner-change-payment-method-subsection';

type ChangePaymentMethodSubsectionProps =
  HomeownerChangePaymentMethodSubsectionProps & {
    setAsDefault: boolean;
    onSetAsDefaultChange: (value: boolean) => void;
  };

export function ChangePaymentMethodSubsection({
  paymentMethods,
  selectedPaymentAccountUniqueId,
  setAsDefault,
  onSelectPaymentMethod,
  onSetAsDefaultChange,
  externalPaymentMethod,
  canPayByWire,
  canPayByZelle,
  onSelectExternalPaymentMethod,
  ...details
}: ChangePaymentMethodSubsectionProps) {
  const availablePaymentMethods = paymentMethods.filter(
    (paymentMethod) => paymentMethod.accountId && paymentMethod.isVerified,
  );
  const selectedPaymentMethod =
    availablePaymentMethods.find(
      (paymentMethod) =>
        paymentMethod.accountId === selectedPaymentAccountUniqueId,
    ) ?? availablePaymentMethods[0];
  const selectedPaymentMethodSummary = selectedPaymentMethod
    ? mapPaymentAccountToPaymentMethodSummary(selectedPaymentMethod)
    : null;

  useEffect(() => {
    if (externalPaymentMethod) return;
    if (!selectedPaymentAccountUniqueId && selectedPaymentMethod?.accountId) {
      onSelectPaymentMethod(selectedPaymentMethod.accountId);
    }
  }, [
    externalPaymentMethod,
    onSelectPaymentMethod,
    selectedPaymentAccountUniqueId,
    selectedPaymentMethod?.accountId,
  ]);

  return (
    <div
      className={
        externalPaymentMethod
          ? 'flex w-full flex-col gap-2xl'
          : 'flex w-full flex-col items-end gap-md'
      }
    >
      <div
        className={
          externalPaymentMethod
            ? 'flex w-full flex-col gap-sm'
            : 'flex w-full flex-col gap-md'
        }
      >
        <Text
          variant="h3"
          fontWeight="semibold"
          className="w-full text-h3-xl leading-[normal]"
        >
          {FAILED_PAYMENTS_STRINGS['change_payment_method.prompt']}
        </Text>

        <FailedPaymentMethodSelector
          paymentMethod={selectedPaymentMethodSummary}
          selectedPaymentAccountUniqueId={selectedPaymentAccountUniqueId}
          paymentType={PaymentPreference.Rent}
          onSelectPaymentMethod={onSelectPaymentMethod}
          externalPaymentMethod={externalPaymentMethod}
          onSelectWireTransfer={
            canPayByWire
              ? () => onSelectExternalPaymentMethod('WireTransfer')
              : undefined
          }
          onSelectZelleTransfer={
            canPayByZelle
              ? () => onSelectExternalPaymentMethod('ZelleTransfer')
              : undefined
          }
        />
      </div>
      {externalPaymentMethod && (
        <WireDetails
          key={externalPaymentMethod}
          externalPaymentMethod={externalPaymentMethod}
          {...details}
        />
      )}
      {!externalPaymentMethod && (
        <Checkbox
          id="failed-payments-set-default-payment-method"
          checked={setAsDefault}
          label={
            FAILED_PAYMENTS_STRINGS['change_payment_method.set_as_default']
          }
          onChange={(checked) => onSetAsDefaultChange(checked === true)}
          variant="primary"
          labelTextClassName="uppercase text-dark-gray"
        />
      )}
    </div>
  );
}
